import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft, Plus, Loader2, DoorOpen, Link2, ShieldCheck, PackageCheck, Play,
  Sparkles, Grid3x3, Box, List, Gauge, Map, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import type { Dwelling, Room, Stitch, Geometry } from "@/api/types";
import { StatusBadge, GeometryBadge, DriftBadge } from "@/components/StatusBadge";
import { GeometryCanvas2D } from "@/components/GeometryCanvas2D";
import { GeometryViewer3D } from "@/components/GeometryViewer3D";
import { CombinedPlan } from "@/components/CombinedPlan";
import { ProcessingOverlay } from "@/components/ProcessingOverlay";
import { Modal, Field, inputCls } from "@/components/Modal";

const ROOM_TYPES = ["living_room", "bedroom", "kitchen", "bathroom", "hallway", "office", "room"];

function ConfidenceBar({ value, testId }: { value: number; testId?: string }) {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 80 ? "bg-emerald-400" : pct >= 60 ? "bg-cyan-400" : pct >= 40 ? "bg-amber-400" : "bg-rose-400";
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-secondary">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-xs text-muted-foreground">{pct}%</span>
    </div>
  );
}

export default function DwellingView() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [floor, setFloor] = useState(0);
  const [planLoading, setPlanLoading] = useState(false);
  const [dossierLoading, setDossierLoading] = useState(false);
  const downloadDwellingPlan = async () => {
    setPlanLoading(true);
    try {
      const blob = await api.dwellingPlanPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `plan_logement.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Échec de la génération du plan. Assurez-vous que des pièces ont une géométrie.");
    } finally {
      setPlanLoading(false);
    }
  };
  const downloadDossier = async () => {
    setDossierLoading(true);
    try {
      const blob = await api.dossierCarrezPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `dossier_carrez.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Échec de la génération du dossier. Assurez-vous que des pièces ont une géométrie.");
    } finally {
      setDossierLoading(false);
    }
  };
  const [view, setView] = useState<"list" | "2d" | "3d" | "combined">(() => {
    const v = new URLSearchParams(window.location.search).get("view") || "";
    return (["list", "combined", "2d", "3d"].includes(v) ? v : "list") as any;
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", room_type: "living_room", floor_index: 0 });

  const { data: dwelling } = useQuery<Dwelling>({ queryKey: ["dwelling", id], queryFn: () => api.getDwelling(id) });
  const { data: rooms = [] } = useQuery<Room[]>({ queryKey: ["rooms", id], queryFn: () => api.listRooms(id) });
  const { data: stitches = [] } = useQuery<Stitch[]>({ queryKey: ["stitches", id], queryFn: () => api.listStitches(id) });

  const roomIds = rooms.map((r) => r._id).join(",");
  const { data: geoms = {} } = useQuery<Record<string, Geometry | null>>({
    queryKey: ["dwelling-geoms", id, roomIds],
    enabled: rooms.length > 0 && view !== "list",
    queryFn: async () => {
      const full = await api.listRoomsFull(id);
      return Object.fromEntries(full.map((r) => [r._id, r.geometry]));
    },
  });

  const createRoomMut = useMutation({
    mutationFn: () => api.createRoom(id, { name: form.name, room_type: form.room_type, floor_index: Number(form.floor_index) }),
    onSuccess: () => { toast.success("Pièce ajoutée"); qc.invalidateQueries({ queryKey: ["rooms", id] }); setOpen(false); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const stitchMut = useMutation({
    mutationFn: () => api.createStitch(id),
    onSuccess: (r: any) => { toast.success(`${r.count} assemblage(s) calculé(s)`); qc.invalidateQueries({ queryKey: ["stitches", id] }); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const validateStitchMut = useMutation({
    mutationFn: (p: { sid: string; status: string }) => api.validateStitch(p.sid, p.status),
    onSuccess: () => { toast.success("Assemblage mis à jour"); qc.invalidateQueries({ queryKey: ["stitches", id] }); },
  });

  const runValMut = useMutation({
    mutationFn: () => api.runValidation(id),
    onSuccess: (r) => {
      toast.success(`Validation: ${r.validation_status} — ${r.issues.length} point(s)`);
      qc.invalidateQueries({ queryKey: ["dwelling", id] });
      navigate(`/validation/${id}`);
    },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const floorRooms = rooms.filter((r) => r.floor_index === floor);
  const metrics = useMemo(() => {
    const avg = rooms.length ? rooms.reduce((s, r) => s + (r.confidence?.overall || 0), 0) / rooms.length : 0;
    const processed = rooms.filter((r) => r.scan_status === "processed").length;
    const stValid = stitches.filter((s) => s.status === "validated").length;
    return { avg, processed, stValid };
  }, [rooms, stitches]);

  if (!dwelling) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8">
      <button onClick={() => navigate(`/projects/${dwelling.project_id}`)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Projet
      </button>

      <div className="rounded-xl border border-border/60 bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{dwelling.name}</h1>
              <StatusBadge status={dwelling.validation_status} testId="dwelling-validation-status" />
            </div>
            <p className="mt-1 uppercase-label text-muted-foreground">{dwelling.levels_count} niveau(x) · {rooms.length} pièce(s)</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="run-validation-button" onClick={() => runValMut.mutate()} disabled={runValMut.isPending} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
              {runValMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Lancer la validation
            </button>
            <button data-testid="goto-validation-button" onClick={() => navigate(`/validation/${id}`)} className="flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm hover:border-primary/40">
              <ShieldCheck className="h-4 w-4" /> Validation
            </button>
            <button data-testid="goto-export-button" onClick={() => navigate(`/export/${id}`)} className="flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm hover:border-primary/40">
              <PackageCheck className="h-4 w-4" /> Export
            </button>
            <button data-testid="dwelling-plan-pdf-button" onClick={downloadDwellingPlan} disabled={planLoading} className="flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm hover:border-primary/40 disabled:opacity-50">
              {planLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Map className="h-4 w-4" />} Plan du logement (PDF)
            </button>
            <button data-testid="dwelling-dossier-carrez-button" onClick={downloadDossier} disabled={dossierLoading} className="flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm hover:border-primary/40 disabled:opacity-50">
              {dossierLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Dossier Carrez (PDF)
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-4">
          {[
            { label: "Confiance moy.", node: <ConfidenceBar value={metrics.avg} testId="dwelling-avg-confidence" />, icon: Gauge },
            { label: "Pièces traitées", node: <span className="font-mono text-lg">{metrics.processed}/{rooms.length}</span>, icon: DoorOpen },
            { label: "Assemblages validés", node: <span className="font-mono text-lg">{metrics.stValid}/{stitches.length}</span>, icon: Link2 },
          ].map((m) => (
            <div key={m.label} className="rounded-lg border border-border/50 bg-background/40 p-4">
              <m.icon className="h-4 w-4 text-muted-foreground" />
              <div className="mt-2">{m.node}</div>
              <div className="uppercase-label mt-1 text-muted-foreground">{m.label}</div>
            </div>
          ))}
        </div>

        {dwelling.ai_summary ? (
          <div data-testid="dwelling-ai-summary" className="mt-4 rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-4">
            <div className="flex items-center gap-2 text-indigo-300"><Sparkles className="h-4 w-4" /><span className="uppercase-label">Revue IA</span></div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">{dwelling.ai_summary}</p>
          </div>
        ) : null}
      </div>

      {/* rooms */}
      <div>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-semibold">Pièces</h2>
          <div className="flex flex-wrap items-center gap-2">
            {dwelling.levels_count > 1 && (
              <select data-testid="dwelling-floor-selector" value={floor} onChange={(e) => setFloor(Number(e.target.value))} className={`${inputCls} w-36`}>
                {Array.from({ length: dwelling.levels_count }).map((_, i) => <option key={i} value={i}>Niveau {i}</option>)}
              </select>
            )}
            <div className="flex rounded-md border border-border p-1" data-testid="dwelling-3d-toggle">
              {([["list", List], ["combined", Map], ["2d", Grid3x3], ["3d", Box]] as const).map(([v, Icon]) => (
                <button key={v} onClick={() => setView(v)} data-testid={`view-${v}-button`} className={`flex items-center gap-1 rounded px-3 py-1.5 text-xs ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <Icon className="h-3.5 w-3.5" /> {v.toUpperCase()}
                </button>
              ))}
            </div>
            <button data-testid="create-room-button" onClick={() => { setForm({ ...form, floor_index: floor }); setOpen(true); }} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90">
              <Plus className="h-4 w-4" /> Ajouter une pièce
            </button>
          </div>
        </div>

        {floorRooms.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">Aucune pièce sur ce niveau.</div>
        ) : view === "combined" ? (
          <div className="rounded-xl border border-border/60 bg-card p-4">
            <div className="mb-3 flex items-center gap-2"><Map className="h-4 w-4 text-primary" /><span className="uppercase-label text-muted-foreground">Plan d'étage combiné — toutes les pièces assemblées</span></div>
            <CombinedPlan rooms={floorRooms} geoms={geoms} />
          </div>
        ) : view === "list" ? (
          <div data-testid="room-list-container" className="space-y-3">
            {floorRooms.map((r) => (
              <div key={r._id} data-testid={`room-row-${r._id}`} className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 border border-primary/20"><DoorOpen className="h-5 w-5 text-primary" /></div>
                  <div>
                    <div className="font-semibold">{r.name}</div>
                    <div className="uppercase-label text-muted-foreground">{r.room_type}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <ConfidenceBar value={r.confidence?.overall || 0} testId={`room-confidence-${r._id}`} />
                  <StatusBadge status={r.scan_status} />
                  <GeometryBadge status={r.geometry_status} testId={`room-geo-${r._id}`} />
                  {r.drift && <DriftBadge drift={r.drift} testId={`room-drift-${r._id}`} />}
                  <button data-testid={`open-room-${r._id}`} onClick={() => navigate(`/rooms/${r._id}`)} className="rounded-md border border-border px-3 py-1.5 text-sm hover:border-primary/40">Ouvrir</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {floorRooms.map((r) => {
              const g = geoms[r._id];
              return (
                <div key={r._id} className="rounded-xl border border-border/60 bg-card p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-semibold">{r.name}</span>
                    <GeometryBadge status={r.geometry_status} />
                  </div>
                  {g ? (
                    view === "2d"
                      ? <GeometryCanvas2D geom={g as any} />
                      : <GeometryViewer3D geom={g as any} rotation={35} showCeiling />
                  ) : (
                    <div className="flex h-32 items-center justify-center rounded-lg bg-background/40 text-xs text-muted-foreground">Pas de géométrie</div>
                  )}
                  <button onClick={() => navigate(`/rooms/${r._id}`)} className="mt-3 w-full rounded-md border border-border py-1.5 text-sm hover:border-primary/40">Ouvrir la pièce</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* stitching */}
      <div data-testid="stitching-status-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Assemblage (Stitching)</h2>
          <button data-testid="run-stitch-button" onClick={() => stitchMut.mutate()} disabled={stitchMut.isPending} className="flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm hover:border-primary/40 disabled:opacity-50">
            {stitchMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Calculer l'assemblage
          </button>
        </div>
        {stitches.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-12 text-center text-muted-foreground">Aucun assemblage. Il faut au moins 2 pièces.</div>
        ) : (
          <div className="space-y-3">
            {stitches.map((s) => (
              <div key={s._id} data-testid={`stitch-row-${s._id}`} className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <Link2 className="h-4 w-4 text-primary" />
                  <span className="font-medium">{s.room_a_name} ↔ {s.room_b_name}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <ConfidenceBar value={s.confidence} />
                  <StatusBadge status={s.status} testId={`stitch-status-${s._id}`} />
                  {s.status !== "validated" && (
                    <button data-testid={`stitch-validate-${s._id}`} onClick={() => validateStitchMut.mutate({ sid: s._id, status: "validated" })} className="rounded-md bg-emerald-500/15 px-3 py-1.5 text-sm text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25">Valider</button>
                  )}
                  {s.status !== "conflict" && s.status !== "validated" && (
                    <button data-testid={`stitch-conflict-${s._id}`} onClick={() => validateStitchMut.mutate({ sid: s._id, status: "conflict" })} className="rounded-md bg-rose-500/15 px-3 py-1.5 text-sm text-rose-400 border border-rose-500/30 hover:bg-rose-500/25">Conflit</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Ajouter une pièce" testId="create-room-modal">
        <form onSubmit={(e) => { e.preventDefault(); createRoomMut.mutate(); }} className="space-y-4">
          <Field label="Nom de la pièce">
            <input data-testid="room-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Living Room" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select data-testid="room-type-input" value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} className={inputCls}>
                {ROOM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Niveau">
              <input data-testid="room-floor-input" type="number" min={0} max={dwelling.levels_count - 1} value={form.floor_index} onChange={(e) => setForm({ ...form, floor_index: Number(e.target.value) })} className={inputCls} />
            </Field>
          </div>
          <button data-testid="room-submit-button" type="submit" disabled={createRoomMut.isPending} className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {createRoomMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Ajouter
          </button>
        </form>
      </Modal>

      <ProcessingOverlay
        open={runValMut.isPending}
        title="Validation du logement en cours"
        stages={["Analyse des reconstructions…", "Contrôle des assemblages…", "Détection des dérives IMU…", "Génération de la revue IA…"]}
        testId="validation-progress-overlay"
      />
    </div>
  );
}
